import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse, HttpContextToken } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { LocalStorageKeys } from '../../shared/helpers/app.constants';
import { AccountService } from '../http-services/account.service';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private readonly RETRY_REQUEST = new HttpContextToken<boolean>(() => false);

  constructor(private readonly accountService: AccountService,
    private readonly router: Router
  ) { }

  intercept<T>(
    request: HttpRequest<T>,
    next: HttpHandler
  ): Observable<HttpEvent<T>> {

    if (this.shouldSkipToken(request.url)) {
      return next.handle(request);
    }
    if (request.url.includes('/Auth/RefreshToken')) {
      const token = localStorage.getItem(LocalStorageKeys.RefreshToken);
      const clonedRequest = request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
      return next.handle(clonedRequest)
    }

    const token = localStorage.getItem(LocalStorageKeys.AuthToken) || localStorage.getItem(LocalStorageKeys.GuestToken);

    if (!token) {
      return this.redirectToSignIn();
    }

    const clonedRequest = request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });

    return next.handle(clonedRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        const alreadyRetried = request.context.get(this.RETRY_REQUEST);
        if (error.status === 401 && !alreadyRetried) {
          const retryRequest = request.clone({
            context: request.context.set(this.RETRY_REQUEST, true)
          });
          return this.handle401Error(retryRequest, next);
        }

        return throwError(() => error);
      })
    );
  }

  private handle401Error<T>(request: HttpRequest<T>, next: HttpHandler): Observable<HttpEvent<T>> {
    return this.accountService.refreshToken().pipe(
      switchMap((newToken: string) => {
        const clonedRequest =
          request.clone({
            setHeaders: {
              Authorization:
                `Bearer ${newToken}`
            }
          });

        return next.handle(
          clonedRequest
        );
      }),

      catchError((error) => {
        this.redirectToSignIn();
        return throwError(
          () => error
        );
      })
    );
  }

  private shouldSkipToken(url: string): boolean {
    const isConfiguredExcluded = environment.urlsToTokenExclude.some(excluded => url.includes(excluded));
    return isConfiguredExcluded;
  }

  private redirectToSignIn() {
    this.router.navigate(['/auth/sign-in'], { queryParams: { returnUrl: this.router.url } });
    return throwError(() => new Error('No token found'));
  }
}
